/**
 * hooks/accounts/useBankAccountForm.ts
 *
 * Form hook for adding a bank account to user's profile.
 * Registered in formHooks as "add_bank_account".
 */

import { useEffect } from "react";
import { COMPONENT, SELECT, TEXT } from "../../constants/constants";
import { API_UGANDAN_BANKS, API_USER_BANK_ACCOUNTS } from "../../constants/apis";
import useGet from "../common/useGet";
import { TSet } from "../common/useSet";
import { reloadData } from "../../components/lists/ListHandler";
import { InfoBox } from "../../components/elements/Elements";
import { showConfetti } from "../../components/elements/ConfettiView";

const OTHER_INTERNATIONAL_CODE = "OTHER_INTERNATIONAL";

export const useBankAccountForm = ({ form, updateFormValues }: any) => {
  const { loadData, data: banksRaw, loading: loadingBanks } = useGet();

  useEffect(() => {
    loadData({ api: API_UGANDAN_BANKS });
  }, []);

  // Handle both plain array and paginated { results: [] } response
  const banks = Array.isArray(banksRaw) ? banksRaw : banksRaw?.results || [];
  const bankOptions = banks.map((b: any) => ({
    id: b.id,
    name: b.name,
  }));

  // Check if user selected the "Other (International)" option
  const selectedBank = banks.find((b: any) => b.id === form?.ugandan_bank);
  const isOtherBank = selectedBank?.code === OTHER_INTERNATIONAL_CODE;

  const fields = [
    {
      type: COMPONENT,
      component: (
        <div className="textCenter p5">
          <div className="font16 bold textDark">Add Bank Account</div>
          <div className="font12 grey mt5">
            This will be used for loan disbursements and withdrawals
          </div>
        </div>
      ),
    },
    {
      field: "ugandan_bank",
      label: "Bank",
      type: SELECT,
      required: true,
      options: bankOptions,
      props: { loading: loadingBanks },
    },
    // Show custom bank name field when "Other (International)" is selected
    ...(isOtherBank
      ? [
          {
            field: "custom_bank_name",
            label: "Bank Name",
            type: TEXT,
            required: true,
            props: {
              placeholder: "e.g. Barclays Bank Kenya",
            },
          },
          {
            field: "swift_code",
            label: "SWIFT / BIC Code",
            type: TEXT,
            required: true,
            props: {
              placeholder: "e.g. BARCKENXXXX",
            },
          },
        ]
      : []),
    {
      field: "account_name",
      label: "Account Holder Name",
      type: TEXT,
      required: true,
      props: {
        placeholder: "Name as it appears on the account",
      },
    },
    {
      field: "account_number",
      label: "Account Number",
      type: TEXT,
      required: true,
      props: {
        placeholder: "e.g. 9030012345678",
        inputMode: "numeric",
      },
    },
    {
      field: "branch_name",
      label: "Branch Name",
      type: TEXT,
      props: {
        placeholder: "e.g. Kampala Main Branch",
        optional: true,
      },
    },
    {
      type: COMPONENT,
      component: (
        <InfoBox>
          Make sure the account details are correct. Incorrect details may
          delay withdrawals and loan disbursements.
        </InfoBox>
      ),
    },
  ];

  const onSubmit = ({ payload }: any): TSet => {
    const bank = banks.find((b: any) => b.id === payload.ugandan_bank);
    const isOther = bank?.code === OTHER_INTERNATIONAL_CODE;

    const params = {
      ...payload,
      bank_name: isOther
        ? payload.custom_bank_name || ""
        : bank?.name || payload.bank_name || "",
      currency: "UGX",
    };

    // Clean up the custom field before sending
    delete params.custom_bank_name;

    return {
      api: API_USER_BANK_ACCOUNTS,
      params,
      successMessage: {
        message: "Bank account added successfully!",
        duration: 5,
      },
      onSuccess: () => {
        reloadData("bank_accounts");
        showConfetti();
      },
    };
  };

  const allowSubmit =
    !!form?.ugandan_bank &&
    !!form?.account_name &&
    !!form?.account_number &&
    // If "Other" is selected, require custom bank name and SWIFT code
    (!isOtherBank || (!!form?.custom_bank_name && !!form?.swift_code));

  return {
    fields,
    onSubmit,
    title: "Add Bank Account",
    submitText: "Save Account",
    allowSubmit,
  };
};